/**
 * Motion primitives.
 *
 * A small, shared vocabulary for animation so every screen moves the same way: a gentle rise-and-fade
 * for content arriving, a light stagger for lists, and a subtle press on tappable things. Built on
 * Framer Motion.
 *
 * ## Restraint is the brief
 *
 * This is a wallet, not a game. Motion here is for ORIENTATION (what just appeared, what responded to
 * my touch), never decoration. Durations sit in the 150-300ms band the UX guidelines call for, and
 * everything animates `transform`/`opacity` only, so it stays on the compositor and never janks a
 * screen full of money.
 *
 * ## Reduced motion is honoured for real
 *
 * `MotionProvider` sets `reducedMotion="user"`, so a participant who has asked their device for less
 * motion gets opacity fades with no movement. The global CSS `prefers-reduced-motion` block is the
 * second belt on the same braces.
 */

import { motion, MotionConfig, type Variants } from 'framer-motion';
import type { ComponentPropsWithoutRef, ReactNode } from 'react';

const EASE = [0.22, 1, 0.36, 1] as const;

/** Content arriving: rise a few pixels and fade in. The default entrance for a card or a section. */
export const fadeUp: Variants = {
  hidden: { opacity: 0, y: 8 },
  show: { opacity: 1, y: 0, transition: { duration: 0.28, ease: EASE } },
};

/** A container that reveals its children one after another, just enough to read as a sequence. */
export const staggerContainer: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.05, delayChildren: 0.02 } },
};

export function MotionProvider({ children }: { children: ReactNode }) {
  return <MotionConfig reducedMotion="user">{children}</MotionConfig>;
}

/** Reveal a block on mount. `delay` lets a page bring sections in one beat apart. */
export function Reveal({
  children,
  className,
  delay = 0,
}: {
  children: ReactNode;
  className?: string;
  delay?: number;
}) {
  return (
    <motion.div
      className={className}
      variants={fadeUp}
      initial="hidden"
      animate="show"
      transition={{ delay }}
    >
      {children}
    </motion.div>
  );
}

/** Wrap a list; pair each child with `StaggerItem` to have them arrive in sequence. */
export function Stagger({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <motion.div className={className} variants={staggerContainer} initial="hidden" animate="show">
      {children}
    </motion.div>
  );
}

export function StaggerItem({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <motion.div className={className} variants={fadeUp}>
      {children}
    </motion.div>
  );
}

/**
 * A button with a light press. The scale is a transform, so it gives tactile feedback without shifting
 * any neighbour's layout. Everything else is a plain `<button>`, so type, disabled, aria and onClick
 * all pass straight through.
 */
export function Pressable({
  children,
  className,
  ...props
}: ComponentPropsWithoutRef<typeof motion.button>) {
  return (
    <motion.button whileTap={{ scale: 0.97 }} className={className} {...props}>
      {children}
    </motion.button>
  );
}
